import cls from "classnames";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

type Props = {
  // 1-based.
  page: number;
  onPageChange: (page: number) => void;
  // The whole list's length, not the current page's.
  total: number;
  pageSize: number;
  // Caption on the left: "312 users · 50 per page". The kit cannot write the
  // sentence, because pluralising it needs the noun the caller is counting.
  summary?: ReactNode;
  className?: string;
};

/**
 * Page controls for an offset-paginated list.
 *
 * A list that fits on one page keeps its summary and loses its controls, so a
 * caller never has to decide whether the pager is worth rendering.
 */
export default function Pager(props: Props) {
  const { t } = useTranslation();
  const pageCount = Math.max(1, Math.ceil(props.total / props.pageSize));

  // Nothing to say about an empty list, and it may be empty because the request
  // failed — in which case the banner above is the whole message.
  if (props.total === 0) {
    return null;
  }

  const goTo = (page: number) => {
    if (page >= 1 && page <= pageCount && page !== props.page) {
      props.onPageChange(page);
    }
  };

  if (pageCount === 1) {
    return props.summary === undefined ? null : (
      <div className={cls(styles.pager, "talaera-pager", props.className)}>
        <span className={styles.summary}>{props.summary}</span>
      </div>
    );
  }

  return (
    <div className={cls(styles.pager, "talaera-pager", props.className)}>
      {props.summary !== undefined && (
        <span className={styles.summary}>{props.summary}</span>
      )}
      <nav className={styles.buttons} aria-label={t("Common.pager.label")}>
        <button
          type="button"
          className={styles.button}
          disabled={props.page <= 1}
          onClick={() => goTo(props.page - 1)}
        >
          {/* Outside the translated word: appended to it, the chevron joins a
              sentence whose order the language can change. */}
          <span aria-hidden>‹ </span>
          {t("Common.actions.previous")}
        </button>
        {pagerItems(props.page, pageCount).map((item, index) => (
          <Fragment key={index}>
            {item === "gap" ? (
              <span className={styles.gap}>…</span>
            ) : (
              <button
                type="button"
                className={cls(styles.button, item === props.page && styles.on)}
                aria-current={item === props.page ? "page" : undefined}
                onClick={() => goTo(item)}
              >
                {item}
              </button>
            )}
          </Fragment>
        ))}
        <button
          type="button"
          className={styles.button}
          disabled={props.page >= pageCount}
          onClick={() => goTo(props.page + 1)}
        >
          {t("Common.actions.next")}
          <span aria-hidden> ›</span>
        </button>
      </nav>
    </div>
  );
}

// Page numbers to render: first, last, and a window around the current page.
function pagerItems(page: number, pageCount: number): (number | "gap")[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const wanted = [1, page - 1, page, page + 1, pageCount].filter(
    p => p >= 1 && p <= pageCount
  );
  const pages = [...new Set(wanted)].sort((a, b) => a - b);

  const items: (number | "gap")[] = [];
  pages.forEach((p, index) => {
    if (index > 0 && p - pages[index - 1] > 1) {
      items.push("gap");
    }
    items.push(p);
  });
  return items;
}
